import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { FULFILLMENT, type Fulfillment, type NewItem, UNIT, type Unit } from "../../products";
import styles from "./products.module.css";

export interface ItemDraft {
  name: string;
  sku: string;
  unit: Unit;
  unit_size: string;
  fulfillment: Fulfillment;
}

export const blankItem = (water: boolean): ItemDraft => ({
  name: "",
  sku: "",
  unit: water ? "litre" : "piece",
  unit_size: water ? "10000" : "1",
  fulfillment: water ? "truck_load" : "parcel",
});

/** An item the API takes: a name, a SKU, and a size above zero (up to 3 decimals). */
export const itemReady = (i: ItemDraft) =>
  i.name.trim() !== "" &&
  i.sku.trim() !== "" &&
  /^\d+(\.\d{1,3})?$/.test(i.unit_size) &&
  !/^0+(\.0+)?$/.test(i.unit_size);

export const toNewItem = (i: ItemDraft): NewItem => ({
  name: i.name.trim(),
  sku: i.sku.trim(),
  unit: i.unit,
  unit_size: i.unit_size,
  fulfillment: i.fulfillment,
});

/** The fields of a new item. Unit, size and fulfilment can't change later: a new item replaces it. */
export function ItemFields({ item, onChange }: { item: ItemDraft; onChange: (item: ItemDraft) => void }) {
  const set = (patch: Partial<ItemDraft>) => onChange({ ...item, ...patch });
  return (
    <>
      <div className={styles.pair}>
        <Input
          label="Item name"
          value={item.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder="10,000 L"
        />
        <Input
          label="SKU"
          value={item.sku}
          onChange={(e) => set({ sku: e.target.value })}
          placeholder="W10K"
          description="Unique in the company."
        />
      </div>
      <div className={styles.pair}>
        <Select
          label="Unit"
          value={item.unit}
          onValueChange={(v) => set({ unit: v as Unit })}
          options={(Object.keys(UNIT) as Unit[]).map((u) => ({ value: u, label: UNIT[u] }))}
        />
        <Input
          label="Size"
          inputMode="decimal"
          value={item.unit_size}
          onChange={(e) => set({ unit_size: e.target.value })}
          description="How many units one item is, e.g. 10000 litres."
        />
      </div>
      <Select
        label="Fulfilment"
        value={item.fulfillment}
        onValueChange={(v) => set({ fulfillment: v as Fulfillment })}
        description={FULFILLMENT[item.fulfillment].help}
        options={(Object.keys(FULFILLMENT) as Fulfillment[]).map((f) => ({ value: f, label: FULFILLMENT[f].label }))}
      />
    </>
  );
}
